import { Page } from '@app/components/page/Page';
import { pushTo } from '@app/core/routing';
import {
  EnrichedVideoEditorEditListItemDto,
  GetFragmentDto,
} from '@app/modules/fragments/fragment.types';
import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { FragmentMediaLoading } from '@app/modules/fragments/screens/view/components/media-detail/FragmentMediaLoading';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { PublishConnectionSelector } from '@app/modules/social-export/components/PublishConnectionSelector';
import { SocialPublishFormActions } from '@app/modules/social-export/components/SocialPublishFormActions';
import { isSamePlatformId } from '@app/modules/social-export/domain/is-same-platform-id';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import { useMediaLimitsIndication } from '@app/modules/social-export/hooks/useMediaLimitsIndication';
import { fragmentToSocialExportPublishSource } from '@app/modules/social-export/lib/social-export-publish-source';
import {
  MediaLimitsAlert,
  MediaSelector,
  NoSourcesAlert,
  PublishPlatformBar,
  SocialExportUploadLayout,
  VideoPreview,
  publishMessages,
} from '@app/modules/social-export/publish';
import { EditCardRenderStatus } from '@app/modules/video-editions';
import { SocialExportGetStationConnectionStateDto } from '@mi-front/api-v2';
import ShareIcon from '@mui/icons-material/Share';
import { Box, CircularProgress, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { SocialExportUploadBreadcrumbs } from './SocialExportUploadBreadcrumbs';
import { type SocialExportPublishForm } from './useSocialExportUploadPage';

export interface SocialExportUploadPageContentProps {
  fragment: GetFragmentDto;
  /** Signed media URL when assets exist; undefined while media is still generating. */
  fragmentMediaUrl: string | undefined;
  /** Connection settings for the selected account; loading only affects the publish form. */
  settingsQuery: UseQueryResult<unknown, unknown>;
  platform: SocialPlatform;
  selectedSource: string;
  onSelectedSourceChange: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  /** True when the selected source is a publishable edit (render pending or completed). */
  hasSelectedPublishableEdit: boolean;
  /** Selected edit when source is a video-editor edit; null for fragment original media. */
  selectedEdit: EnrichedVideoEditorEditListItemDto | null;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm;
  onUpload: (data: Record<string, unknown>) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  onSelectedConnectionChange: (id: string | null) => void;
}

/** Layout and conditional UI once fragment and edits are loaded (media URL / render may still be pending). */
export function SocialExportUploadPageContent({
  fragment,
  fragmentMediaUrl,
  settingsQuery,
  platform,
  selectedSource,
  onSelectedSourceChange,
  edits,
  isFragmentSourceAllowed,
  hasAvailableSources,
  showMediaSelector,
  hasSelectedPublishableEdit,
  selectedEdit,
  renderedVideoUrl,
  PublishForm,
  onUpload,
  connections,
  selectedConnectionId,
  onSelectedConnectionChange,
}: SocialExportUploadPageContentProps) {
  const { $t } = useIntl();
  const publishSource = fragmentToSocialExportPublishSource(fragment);
  const { platforms: availablePlatforms } = useAvailableSocialExportPlatformsForStation(
    fragment.stationId,
  );

  const isFragmentSource =
    selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && isFragmentSourceAllowed;

  const effectiveMediaUrl = isFragmentSource
    ? (fragmentMediaUrl ?? null)
    : selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? renderedVideoUrl
      : null;

  /** Fragment original and publishable edits can start before media/render is ready. */
  const canPublish = isFragmentSource || hasSelectedPublishableEdit;

  // Fixed title: the platform is a selection inside the page, not the page identity.
  const title = $t(publishMessages.publishToSocialNetworks);
  const areSettingsReady = !settingsQuery.isLoading && settingsQuery.data !== undefined;

  const selectedConnection = useMemo(
    () => connections.find((c) => c.id === selectedConnectionId),
    [connections, selectedConnectionId],
  );
  // Duration is known for the fragment source; edit renders do not expose duration on list DTOs.
  const durationMs = isFragmentSource
    ? fragment.chunks.reduce((sum, chunk) => sum + chunk.duration, 0)
    : undefined;
  const mediaLimitsIndication = useMediaLimitsIndication(selectedConnection, durationMs);

  const editIdForUrl =
    selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE ? selectedSource : undefined;

  const handlePlatformSwitch = (platformId: string) => {
    if (isSamePlatformId(platformId, platform.id)) {
      return;
    }
    pushTo(routeGenerator.upload(fragment, { id: platformId }, editIdForUrl), true);
  };

  const platformSwitcher = (
    <ContentCluster message={publishMessages.platformSwitcherLabel} divider={false}>
      <PublishPlatformBar
        platforms={availablePlatforms}
        selectedPlatformId={platform.id}
        onSelectPlatform={handlePlatformSwitch}
      />
    </ContentCluster>
  );

  const videoPreview = effectiveMediaUrl ? (
    <VideoPreview videoUrl={effectiveMediaUrl} />
  ) : isFragmentSource ? (
    <ContentCluster message={publishMessages.videoPreviewTitle} divider={false}>
      <FragmentMediaLoading fragment={fragment} />
    </ContentCluster>
  ) : selectedEdit?.renderJob ? (
    <ContentCluster message={publishMessages.videoPreviewTitle} divider={false}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <EditCardRenderStatus renderJob={selectedEdit.renderJob} />
      </Box>
    </ContentCluster>
  ) : null;

  return (
    <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
      <Page htmlTitle={title}>
        <Page.Header
          icon={<ShareIcon />}
          title={title}
          breadcrumbs={<SocialExportUploadBreadcrumbs fragment={fragment} />}
        />
        <Page.Content maxWidth="lg">
          {connections.length === 0 ? (
            <>
              {platformSwitcher}
              <Typography color="text.secondary">
                <FormattedMessage
                  id="socialExport.connections.noneAssignedForStation"
                  defaultMessage="No hay cuentas conectadas asignadas a esta emisora para esta plataforma."
                />
              </Typography>
            </>
          ) : !hasAvailableSources ? (
            <>
              {platformSwitcher}
              <NoSourcesAlert
                description={publishMessages.panoramaNoSourcesFragment}
                linkTo={`/fragments/${fragment.id}/edits`}
              />
            </>
          ) : (
            <SocialExportUploadLayout
              platform={platformSwitcher}
              media={
                showMediaSelector ? (
                  <MediaSelector
                    value={selectedSource}
                    onChange={onSelectedSourceChange}
                    edits={edits}
                    includeFragmentOption={isFragmentSourceAllowed}
                    includeInProgressRenders
                  />
                ) : null
              }
              preview={videoPreview}
              form={
                <>
                  {mediaLimitsIndication ? (
                    <MediaLimitsAlert indication={mediaLimitsIndication} />
                  ) : null}
                  {!canPublish && selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
                    ? $t(publishMessages.mediaSelectorRenderNotReadyFragment)
                    : canPublish &&
                      !mediaLimitsIndication?.blocksPublish && (
                        <>
                          <PublishConnectionSelector
                            connections={connections}
                            selectedConnectionId={selectedConnectionId}
                            onSelectedConnectionChange={onSelectedConnectionChange}
                            platform={platform}
                          />
                          {areSettingsReady ? (
                            <PublishForm
                              key={`${platform.id}:${selectedConnectionId ?? ''}`}
                              source={publishSource}
                              videoUrl={effectiveMediaUrl ?? ''}
                              onUpload={onUpload}
                              defaultSettings={settingsQuery.data}
                              connectionId={selectedConnectionId ?? ''}
                              actions={<SocialPublishFormActions platform={platform} />}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                              <CircularProgress />
                            </Box>
                          )}
                        </>
                      )}
                </>
              }
            />
          )}
        </Page.Content>
      </Page>
    </SocialExportContext.Provider>
  );
}
